import { AVATAR_MIME_TYPES } from '@pm/schemas';
import { Avatar, Button } from '@pm/ui';
import { Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { squareImageBase64 } from '@/lib/image';
import { useToast } from '@/lib/toast';

/** Foto de perfil: recorta em quadrado e reduz para 256px no navegador antes de enviar. */
export function AvatarUploader({
  name,
  url,
  onUpload,
  onRemove,
  disabled,
}: {
  name: string;
  url: string | null;
  onUpload: (body: { mime_type: string; data_base64: string }) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
  disabled?: boolean;
}) {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(null, 'Escolha uma imagem (PNG, JPG ou WEBP).');
      return;
    }
    setBusy('upload');
    try {
      await onUpload(await squareImageBase64(file));
      toast.success('Foto atualizada');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar name={name} src={url} size="xl" />
      <div className="flex flex-col gap-2">
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={[...AVATAR_MIME_TYPES, 'image/heic', 'image/gif'].join(',')}
            className="hidden"
            onChange={(e) => {
              void pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled}
            loading={busy === 'upload'}
            onClick={() => fileRef.current?.click()}
          >
            <Upload /> {url ? 'Trocar foto' : 'Enviar foto'}
          </Button>
          {url ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              loading={busy === 'remove'}
              onClick={() => {
                setBusy('remove');
                onRemove()
                  .then(() => toast.success('Foto removida'))
                  .catch((err: unknown) => toast.error(err))
                  .finally(() => setBusy(null));
              }}
            >
              <Trash2 /> Remover
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-text-3">
          A imagem é recortada no centro e reduzida para 256×256.
        </p>
      </div>
    </div>
  );
}
